import { AfterViewChecked, Component, inject, OnInit, QueryList, ViewChildren } from '@angular/core';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { TranslateService } from '@ngx-translate/core';
import { format } from 'date-fns';
import { CartRow, CartService, Customer, PriceService, Product, ProductSelectDropdownComponent, SelectedCustomerService } from 'gung-standard';
import { first, forkJoin, of, switchMap } from 'rxjs';
import { HlDeliveryDateService } from '../../services/hl-delivery-date.service';
import { HlDisplayProductListConfigService } from '../../services/hl-display-product-list-config.service';

@Component({
    selector: 'app-hl-display-checkout-add-row-modal',
    templateUrl: './hl-display-checkout-add-row-modal.component.html',
    styleUrls: ['./hl-display-checkout-add-row-modal.component.css'],
    standalone: false
})
export class HlDisplayCheckoutAddRowModalComponent implements OnInit, AfterViewChecked {
  public activeModal = inject(NgbActiveModal);
  private cartService = inject(CartService);
  private selectedCustomerService = inject(SelectedCustomerService);
  private deliveryDateService = inject(HlDeliveryDateService);
  private productListConfig = inject(HlDisplayProductListConfigService);
  private priceService = inject(PriceService);
  private translateService = inject(TranslateService);

  private orderLineItemNotInJeevesProductId = '155035';
  private orderLineItemNotInJeevesNextIndex = 0;
  protected selectedCustomer: Customer;
  cartRows: CartRow[] = [];
  currentLang = this.translateService.currentLang;

  modalRows: {
    productId: string;
    productPartialId: string;
    quantity: number;
    price: number;
    discount: number;
    name: string;
  }[] = [];
  discountFactor: number = 0;

  @ViewChildren('productSelectInput') productSelectInputs!: QueryList<ProductSelectDropdownComponent>;
  shouldFocusLastInput = false;

  getSearchTerms: (product: Product) => string[] = (product: Product) => {
    const terms = [...this.productListConfig.getSearchTerms(product)];

    if (this.selectedCustomer?.extra?.kuarMapped?.[product.id]?.artnrkund) {
      terms.push(this.selectedCustomer.extra?.kuarMapped?.[product.id]?.artnrkund);
    }

    return terms;
  };

  ngOnInit() {
    this.cartService
      .getCurrentCart()
      .pipe(
        first(),
        switchMap(cartRows =>
          forkJoin({
            cartRows: of(cartRows),
            selectedCustomer: this.selectedCustomerService.getSelectedCustomer().pipe(first())
          })
        )
      )
      .subscribe(({ cartRows, selectedCustomer }) => {
        this.cartRows = cartRows;
        this.selectedCustomer = selectedCustomer;
      });
  }

  ngAfterViewChecked(): void {
    if (this.shouldFocusLastInput) {
      const dropdowns = this.productSelectInputs.toArray();
      const lastDropdown = dropdowns[dropdowns.length - 1];

      const input = lastDropdown?.inputTextElement?.nativeElement;

      if (input) {
        input.focus();
        this.shouldFocusLastInput = false;
      }
    }
  }

  removeRow(index) {
    this.modalRows.splice(index, 1);
  }

  addNewRow() {
    const prodPartialId = this.getModalRowNewPartialId(this.orderLineItemNotInJeevesProductId);
    this.modalRows.push({
      productId: this.orderLineItemNotInJeevesProductId,
      productPartialId: prodPartialId ? prodPartialId.toString() : undefined,
      quantity: 1,
      discount: 0,
      name: '',
      price: undefined
    });

    this.shouldFocusLastInput = true;
  }

  selectProduct(product: Product, row: any) {
    row.productPartialId = this.getModalRowNewPartialId(product.id);
    row.productId = product.id;
    row.name = product.name;
    this.priceService
      .getCurrentCustomerPrice(product.id)
      .pipe(first())
      .subscribe(price => {
        row.price = price.customerNetPrice.value * row.quantity;
      });
  }

  getModalRowNewPartialId(productId: string): number {
    const cartRowsItemsMatch = this.cartRows.filter(cartRow => cartRow.productId === productId);
    const modalRowsItemsMatch = this.modalRows.filter(row => row.productId === productId);

    if (cartRowsItemsMatch.length > 0 || modalRowsItemsMatch.length > 0) {
      const modalRowsItemsMatchPartialIds = modalRowsItemsMatch.map(
        row => Number.parseInt(row.productPartialId, 10) || 0
      );
      const cartRowsItemsMatchPartialIds = cartRowsItemsMatch.map(
        cartRow => Number.parseInt(cartRow.productPartialId, 10) || 0
      );
      return Math.max(...cartRowsItemsMatchPartialIds, ...modalRowsItemsMatchPartialIds) + 1;
    }
    return 0;
  }

  submitRows() {
    if (this.modalRows.length === 0) {
      this.activeModal.close();
      return;
    }

    this.selectedCustomerService
      .getSelectedCustomer()
      .pipe(
        first(),
        switchMap(customer => this.deliveryDateService.getDeliveryDates(customer.extra.kus.levsattkod).pipe(first()))
      )
      .subscribe(deliveryDates => {
        const firstDateAvailable = deliveryDates?.find(date => date.valid);
        const deliveryDate = firstDateAvailable?.date ? firstDateAvailable?.date : new Date();

        for (const modalRow of this.modalRows) {
          const theOrp = {
            vb_pris: (modalRow.price * (1 / (1 + this.discountFactor))).toFixed(2),
            // ordantal: modalRow.quantity,
            rabatt1: modalRow.discount,
            artbeskr: modalRow.name,
            ordberlevdat: format(deliveryDate, 'yyyy-MM-dd')
          };

          this.cartService.addToCart(modalRow.productId, modalRow.quantity, undefined, modalRow.productPartialId);

          // This doesn't call SalesCartCartService, thats why the workaround above
          this.cartService.setExtra(
            modalRow.productId,
            {
              orp: theOrp,
              _discountCustomerFinancialGroupInfo: {
                appliedFactor: this.discountFactor,
                appliedTo: modalRow.price,
                appliedFrom: 'HlDisplayCheckoutAddRowModalComponent'
              }
            },
            undefined,
            modalRow.productPartialId
          );
        }

        this.activeModal.close();
      });
  }

  validateQuantity(value: number, rowIndex: number): void {
    if (value <= 0) {
      this.modalRows[rowIndex].quantity = 1;
    }
  }

  formatProductName(product: Product): string {
    let description = '';

    if (product.extra.ar.artbeskrspec) {
      description = ' - (' + product.extra.ar.artbeskrspec + ')';
    }

    return product.id + ' - ' + product.extra.ar.artbeskr + description;
  }
}
